/**
 * lazyWithRetry - React.lazy wrapper that handles stale-chunk failures.
 *
 * Problem: after a Vercel deploy, any open tab still has the old index.html
 * referencing old chunk hashes. Clicking nav triggers a dynamic import for a
 * chunk that no longer exists on the CDN. The native `React.lazy` has no
 * recovery; the Suspense fallback just hangs or the page stays blank until
 * the user manually reloads.
 *
 * Fix: if the dynamic import rejects, force a one-shot hard reload so the
 * browser pulls the fresh index.html + new chunk hashes. A sessionStorage
 * flag guards against reload loops, so a genuine chunk bug still surfaces
 * via the error boundary on the second attempt.
 *
 * Also detects a ChunkLoadError via the error message, since the rejected
 * reason can vary across browsers (TypeError "Failed to fetch", "Importing
 * a module script failed", etc.).
 */

import { lazy, type ComponentType } from 'react'

const RELOAD_FLAG_KEY = 'lev-chunk-reload-attempted'

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  return (
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  )
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory()
      // Successful load resets the reload guard so a later deploy can reload again.
      try {
        window.sessionStorage.removeItem(RELOAD_FLAG_KEY)
      } catch {
        // sessionStorage may be unavailable (SSR, private mode quota). Ignore.
      }
      return mod
    } catch (error) {
      if (isChunkLoadError(error)) {
        let hasReloaded = false
        try {
          hasReloaded = window.sessionStorage.getItem(RELOAD_FLAG_KEY) === '1'
        } catch {
          hasReloaded = false
        }
        if (!hasReloaded) {
          try {
            window.sessionStorage.setItem(RELOAD_FLAG_KEY, '1')
          } catch {
            // ignore
          }
          window.location.reload()
          // Return a placeholder module while the reload fires. The component
          // will never actually render because the page is tearing down.
          return { default: (() => null) as unknown as T }
        }
      }
      throw error
    }
  })
}
